import * as React from "react";
import { useCallback, useEffect } from "react";
import { Button, Gapped, Modal, Radio, RadioGroup, Toast } from "react-ui";
import { useDispatch } from "react-redux";

import { hideModalActionCreator } from "../../FormModalAction";
import {
    CombinatorType,
    CombineFufResult,
    FufCombineMethod,
    SumCombineMethod,
} from "../../../../../../../Common/CandyApiClient/Types/DraftActions/CombineFufResult";
import { CustomFile } from "../../../../../Helpers/file";
import { ModalStateInfo } from "../../IModalState";
import { ServerErrorResponse } from "../../../../../../../Common/CandyApiClient/Types/DraftActions/FileProperties";
import { isNotNullOrEmpty } from "../../../../../../../Common/TypingUtils";
import { useAsyncEffect } from "../../../../../../../Common/UseAsyncEffect";
import { extractFileExtension, FileProps, PatchFufSteps, TableType, UploadFileStatuses } from "../Utils/Types";
import { ModalText, StepPage } from "../Utils/Styles/Modals.styles";
import {
    XML_UPLOAD_CLICK_CLOSE,
    XML_UPLOAD_FILE,
    XML_UPLOAD_FILES,
    XML_UPLOAD_SELECT_ADD,
    XML_UPLOAD_SELECT_REPLACE,
} from "../../../../../Controls/metricsAction";
import { useMetrics } from "../../../../../Controls/Internals/Metrics/MetricsProviderContext";
import { UploadedRSVFileInfo } from "../../../../ActionCreators/FufUploadActionCreator";
import { useUploadFile } from "../Utils/UploadFileRedisign/UseUploadFile";
import {
    getAcceptFilesError,
    getStepHeaderContent,
    getTableTypeHeaderContent,
    invalidFileErrorText,
    invalidFileErrorTextRSV,
    periodWarningText,
    periodWarningText2,
} from "../Utils/TextContent";
import { CombineMethodPicker } from "../Utils/CombineMethodPicker/CombineMethodPicker";
import { MergeNotice } from "../UploadSection/MergeNotice/MergeNotice";
import { ModalLoader } from "../Utils/ModalLoader/ModalLoader";
import { smallModalWidth, wideModalWidth } from "../Utils/Styles/ModalWidth";
import { useExternalEventManager } from "../../../../../Controls/FormObjectProvider";
import { formPreferredInterdocumentTransferEvent } from "../../../EventManagement/ExternalEvents/ExternalEvents";
import { FileException, FileExceptionWarning } from "../Utils/UploadFileRedisign/UploadFile.styles";
import { ICombineByTransfers } from "../UploadSection/Types";

export interface rsvPreferredTransfers {
    typeId: "formsRsvTransfers";
    role: "curPeriod" | "yearPeriod" | "allPrevPeriod";
    onEnd: () => void;
}

export interface PatchFufByAttachmentModalProps {
    modalSettings?: ModalStateInfo;
    currentGfv: string;
    reloadForm: (path?: string) => void;
    getFileProperties: (file: CustomFile | undefined) => Promise<UploadedRSVFileInfo>;
    importXmls: (
        driveUris: string[],
        combineMethod: FufCombineMethod,
        combinatorType: CombinatorType,
        isImportByFuf: boolean,
        args?: string[]
    ) => Promise<CombineFufResult | undefined>;
    combineMultipleWithTransfer: (
        files: ICombineByTransfers[],
        abortSignal?: AbortSignal
    ) => Promise<ServerErrorResponse | undefined>;
}

export const UploadXmlInTable = ({
    modalSettings,
    reloadForm,
    currentGfv,
    importXmls,
    getFileProperties,
}: PatchFufByAttachmentModalProps) => {
    const dispatch = useDispatch();
    const track = useMetrics();
    const externalEventManager = useExternalEventManager();

    const [loading, setLoading] = React.useState(false);
    const [step, setStep] = React.useState<PatchFufSteps>(PatchFufSteps.SelectCombineFufMethod);
    const [modalHeader, setModalHeader] = React.useState<React.ReactNode>(<>Загрузка файлов</>);
    const [fufCombineMethod, setFufCombineMethod] = React.useState<FufCombineMethod>(FufCombineMethod.AddToEnd);
    const [sumCombineMethod, setSumCombineMethod] = React.useState<SumCombineMethod>(SumCombineMethod.replaceSum);

    const memoizedFilter = useCallback(filterValidFiles, []);
    const memoizedCheckValidFile = useCallback(checkValidFile, []);

    const { filesContainer, uploadedFiles, validDriveURLs, addUserFilesToDrive } = useUploadFile<string>({
        filterValidDriveUrl: memoizedFilter,
        checkValidFile: memoizedCheckValidFile,
    });

    function pushToast() {
        Toast.push("Не удалось загрузить файл", {
            label: "Повторить",
            handler: refreshUpload,
        });
    }

    function filterValidFiles(fileToRemove: FileProps, url: string) {
        return url !== fileToRemove.driveUri;
    }

    React.useEffect(() => {
        setModalHeader(getStepHeaderContent(fufCombineMethod));
    }, [step]);

    useAsyncEffect(async () => {
        if (modalSettings?.currentInstancesCount === 0) {
            setStep(PatchFufSteps.SelectFiles);
            setFufCombineMethod(FufCombineMethod.AddToEnd);
            await addUserFilesToDrive();
        } else {
            setStep(PatchFufSteps.SelectCombineFufMethod);
        }
    }, []);

    useEffect(() => {
        setModalHeader(getTableTypeHeaderContent(modalSettings?.tableType));
    }, []);

    const onClose = async () => {
        track(XML_UPLOAD_CLICK_CLOSE, { path: modalSettings?.pathToTable });
        dispatch(hideModalActionCreator());
    };

    const onDownloadFileHandler = async () => {
        const invalidFilesCount = uploadedFiles.length - validDriveURLs.length;

        track(XML_UPLOAD_FILES, {
            path: modalSettings?.pathToTable,
            filesCount: uploadedFiles.length,
            validFilesCount: validDriveURLs.length,
            invalidFilesCount: invalidFilesCount,
        });
        if (validDriveURLs.length) {
            setStep(PatchFufSteps.LoadToForm);
            const isReplaceMethod =
                fufCombineMethod === FufCombineMethod.ReplaceThenMerge ||
                fufCombineMethod === FufCombineMethod.ReplaceThenAddToEnd;
            const combineMethod = isReplaceMethod
                ? fufCombineMethod
                : (modalSettings?.combineType ?? FufCombineMethod.AddToEnd);
            if (modalSettings?.useTransfersAndCheckUnitExist) {
                await uploadFilesInRsv(combineMethod);
            } else {
                await uploadFiles(modalSettings?.pathToTable ?? "", combineMethod, modalSettings?.isImportByFuf);
            }
        }
    };

    async function refreshUpload() {
        Toast.close();
        await onDownloadFileHandler();
    }

    /** Валидируем файл и возвращаем новое состояние */
    async function checkValidFile(
        file: FileProps,
        setValidDriveURLs: React.Dispatch<React.SetStateAction<string[]>>
    ): Promise<FileProps> {
        let errorMessageText: string = "";
        let warningMessageText: string = "";
        if (!modalSettings?.acceptFiles?.includes(extractFileExtension(file.uploadFile.name))) {
            errorMessageText = getAcceptFilesError(modalSettings?.acceptFiles);
        }

        const driveUriGuard = file.driveUri;
        if (file.id && driveUriGuard && !errorMessageText) {
            const properties: UploadedRSVFileInfo = await getFileProperties(file.uploadFile);
            const isPageExistsInFile = (): boolean => {
                switch (modalSettings?.rsvSection) {
                    case 1:
                        return properties.fileProperties?.razd1_exists === "True";
                    case 2:
                        return properties.fileProperties?.razd2_exists === "True";
                    case 3:
                        return properties.fileProperties?.razd3_exists === "True";
                    case 4:
                        return properties.fileProperties?.razd4_exists === "True";
                    default:
                        return false;
                }
            };
            if (
                properties.fileProperties?.gfv !== currentGfv &&
                !modalSettings?.acceptGFVs?.includes(properties.fileProperties?.gfv as string)
            ) {
                errorMessageText = modalSettings?.useTransfersAndCheckUnitExist
                    ? invalidFileErrorTextRSV
                    : invalidFileErrorText;
            } else if (
                modalSettings?.useTransfersAndCheckUnitExist &&
                modalSettings?.acceptGFVs?.includes(properties.fileProperties?.gfv ?? "") &&
                !isPageExistsInFile()
            ) {
                errorMessageText =
                    modalSettings?.sectionInfo?.fileUploadNotSectionFoundErrorMessage ?? invalidFileErrorText;
            } else {
                if (modalSettings?.rsvSection && properties.fileProperties?.PeriodCode !== modalSettings?.period) {
                    warningMessageText =
                        modalSettings?.rsvSection === 3
                            ? periodWarningText(
                                  properties.fileProperties?.PeriodCode ?? "",
                                  modalSettings?.period ?? ""
                              )
                            : periodWarningText2(
                                  properties.fileProperties?.PeriodCode ?? "",
                                  modalSettings?.period ?? ""
                              );
                }
                setValidDriveURLs(value => [...value, driveUriGuard]);
            }
        }
        const status = errorMessageText ? UploadFileStatuses.error : UploadFileStatuses.success;
        track(XML_UPLOAD_FILE, {
            path: modalSettings?.pathToTable,
            uploadFile: file.uploadFile.name,
            size: file.uploadFile.size,
            status: status,
            errorMessage: errorMessageText,
        });
        return {
            id: file.id,
            uploadFile: file.uploadFile,
            status: status,
            errorMessage: <FileException>{errorMessageText}</FileException>,
            warningMessage: <FileExceptionWarning>{warningMessageText}</FileExceptionWarning>,
            driveUri: file.driveUri,
        };
    }

    const uploadFiles = async (pathToTable: string, combineMethod: FufCombineMethod, isImportByFuf?: boolean) => {
        setLoading(true);
        const combinatorType = modalSettings?.combinatorType ?? CombinatorType.Default;
        const response = await importXmls(
            validDriveURLs,
            combineMethod,
            combinatorType,
            isImportByFuf ?? combinatorType !== CombinatorType.Default,
            combinatorType === CombinatorType.Default ? [] : [pathToTable]
        );
        if (response !== undefined) {
            if (response.addedItemsCount !== 0 || response.updatedItemsCount !== 0) {
                reloadForm(modalSettings?.navigationPath ?? pathToTable);
                return;
            } else {
                await onClose();
                Toast.push("В файле нет данных для загрузки");
            }
        } else {
            setStep(PatchFufSteps.SelectFiles);
            pushToast();
        }
        setLoading(false);
    };

    const uploadFilesInRsv = async (combineMethod: FufCombineMethod) => {
        setLoading(true);
        const response = await importXmls(validDriveURLs, combineMethod, CombinatorType.Default, false, [
            sumCombineMethod,
            `section${modalSettings?.rsvSection}`,
        ]);
        if (response !== undefined) {
            // последователдьно вызываем 3 переноса друг за другом. по заверениям КОПФ-а у них было именно так.
            // порядок менять нельзя
            try {
                const callСurPeriod = () => {
                    externalEventManager.triggerExternalEvent(
                        formPreferredInterdocumentTransferEvent({
                            typeId: "formsRsvTransfers",
                            role: "curPeriod",
                            onEnd: callAllPrevPeriod,
                        })
                    );
                };

                const callAllPrevPeriod = () => {
                    externalEventManager.triggerExternalEvent(
                        formPreferredInterdocumentTransferEvent({
                            typeId: "formsRsvTransfers",
                            role: "allPrevPeriod",
                            onEnd: callYearPeriod,
                        })
                    );
                };

                const callYearPeriod = () => {
                    externalEventManager.triggerExternalEvent(
                        formPreferredInterdocumentTransferEvent({
                            typeId: "formsRsvTransfers",
                            role: "yearPeriod",
                            onEnd: () => reloadForm(modalSettings?.navigationPath),
                        })
                    );
                };
                callСurPeriod();
                return;
            } catch {
                setStep(PatchFufSteps.SelectFiles);
                pushToast();
            }
        } else {
            setStep(PatchFufSteps.SelectFiles);
            pushToast();
        }
        setLoading(false);
    };

    const getDriveUrls = () =>
        uploadedFiles
            .map(file => (file.status !== UploadFileStatuses.error ? file.driveUri : undefined))
            .filter(isNotNullOrEmpty);

    const setCombineMethodAndUploadFiles = async (combineMethod: FufCombineMethod) => {
        if (
            modalSettings?.useTransfersAndCheckUnitExist &&
            combineMethod !== FufCombineMethod.ReplaceThenAddToEnd &&
            combineMethod !== FufCombineMethod.ReplaceThenMerge &&
            modalSettings?.rsvSection === 3
        ) {
            setStep(PatchFufSteps.SelectFiles);
            setFufCombineMethod(combineMethod);
            await addUserFilesToDrive();
        } else {
            setStep(PatchFufSteps.SelectFiles);
            setFufCombineMethod(combineMethod);
            await addUserFilesToDrive();
        }
    };

    const clickOnReplaceButton = async () => {
        track(XML_UPLOAD_SELECT_REPLACE, { path: modalSettings?.pathToTable });
        await setCombineMethodAndUploadFiles(getReplaceMethod());
    };

    const setPreviousStep = async () => {
        if (modalSettings?.useTransfersAndCheckUnitExist && modalSettings?.rsvSection === 3) {
            setStep(PatchFufSteps.SelectFiles);
        } else {
            setStep(PatchFufSteps.SelectCombineFufMethod);
        }
    };
    const clickOnAddButton = async () => {
        track(XML_UPLOAD_SELECT_ADD, { path: modalSettings?.pathToTable });
        await setCombineMethodAndUploadFiles(FufCombineMethod.AddToEnd);
    };

    const onApplyUploadFiles = async () => {
        await setCombineMethodAndUploadFiles(getReplaceMethod());
        setStep(PatchFufSteps.SelectFiles);
    };

    const onChangeRadioGroup = async (value: SumCombineMethod) => {
        setSumCombineMethod(value);
    };

    const confirmMerge = async () => {
        await setCombineMethodAndUploadFiles(FufCombineMethod.Merge);
    };

    const onPickSumMethod = () => {
        setStep(PatchFufSteps.SelectCombineSumMethod);
    };

    const getReplaceMethod = (): FufCombineMethod =>
        modalSettings?.combinatorType === CombinatorType.XmlCombinatorByPath
            ? FufCombineMethod.ReplaceThenAddToEnd
            : FufCombineMethod.ReplaceThenMerge;

    const isSomeFileLoading = uploadedFiles.some(
        file => file.status === UploadFileStatuses.new || file.status === UploadFileStatuses.progress
    );

    const isButtonLoadDisabled = loading || !Boolean(getDriveUrls().length) || isSomeFileLoading;

    const isHaveValidAndInvalidFile =
        uploadedFiles.some(item => item.status === UploadFileStatuses.error) &&
        uploadedFiles.some(item => item.status === UploadFileStatuses.success);

    const isStaticAndSelectFufMethod =
        step === PatchFufSteps.SelectCombineFufMethod && modalSettings?.tableType === TableType.static;

    const isDynamicAndSelectFufMethod =
        step === PatchFufSteps.SelectCombineFufMethod && modalSettings?.tableType === TableType.dynamic;

    const isDynamicAndSelectCombineSumMethod =
        step === PatchFufSteps.SelectCombineSumMethod && modalSettings?.tableType === TableType.dynamic;

    const isRSVAndSelectFufMethod =
        step === PatchFufSteps.SelectCombineFufMethod && modalSettings?.useTransfersAndCheckUnitExist;

    if (isRSVAndSelectFufMethod) {
        return (
            <MergeNotice
                onClose={onClose}
                onSubmit={confirmMerge}
                /* eslint-disable-next-line no-irregular-whitespace */
                headerText={`Загрузить таблицу из раздела ${
                    modalSettings?.rsvSection ? modalSettings?.rsvSection : 3
                }?`}
                bodyText={`Новые данные ${
                    modalSettings?.rsvSection === 3 ? "будут объединены с текущими" : "заменят данные в разделе"
                }`}
            />
        );
    }

    if (isStaticAndSelectFufMethod) {
        return (
            <MergeNotice
                onClose={onClose}
                onSubmit={onApplyUploadFiles}
                headerText={modalHeader}
                bodyText="Новые данные заменят данные в таблице"
            />
        );
    }

    return (
        <Modal
            width={isStaticAndSelectFufMethod ? smallModalWidth : wideModalWidth}
            ignoreBackgroundClick
            onClose={onClose}
            noClose={step === PatchFufSteps.LoadToForm}>
            <Modal.Header data-tid={"ModalHeader"}>{modalHeader}</Modal.Header>
            <Modal.Body style={{ paddingBottom: 16 }}>
                <ModalLoader loading={loading}>
                    <>
                        <StepPage visible={isDynamicAndSelectFufMethod}>
                            <CombineMethodPicker
                                onMergeClick={
                                    modalSettings?.combineType === FufCombineMethod.Merge ? clickOnAddButton : undefined
                                }
                                onAddToEndClick={
                                    modalSettings?.combineType === FufCombineMethod.AddToEnd ||
                                    modalSettings?.combineType === undefined
                                        ? clickOnAddButton
                                        : undefined
                                }
                                onReplaceClick={clickOnReplaceButton}
                            />
                        </StepPage>
                        <StepPage visible={isDynamicAndSelectCombineSumMethod}>
                            <ModalText data-tid="subheaderModal">Что делать с суммами?</ModalText>
                            <RadioGroup onValueChange={onChangeRadioGroup} name="radioGroup" value={sumCombineMethod}>
                                <Gapped data-tid="CombineMethod" vertical gap={8}>
                                    <Radio value={SumCombineMethod.replaceSum}>
                                        Заменить суммы в отчете суммами из файла
                                    </Radio>
                                    <Radio value={SumCombineMethod.addSum}>
                                        Сложить суммы в отчете и суммы из файла
                                    </Radio>
                                    <Radio value={SumCombineMethod.ignoreSum}>
                                        Не заменять суммы в отчете суммами из файла
                                    </Radio>
                                </Gapped>
                            </RadioGroup>
                        </StepPage>
                        <StepPage visible={step === PatchFufSteps.SelectFiles}>{filesContainer}</StepPage>
                    </>
                </ModalLoader>
            </Modal.Body>
            <StepPage visible={isStaticAndSelectFufMethod || step !== PatchFufSteps.SelectCombineFufMethod}>
                <Modal.Footer panel sticky>
                    <StepPage visible={isDynamicAndSelectCombineSumMethod}>
                        <Gapped gap={16}>
                            <Button
                                data-tid={"UploadButtonInPickSum"}
                                disabled={isButtonLoadDisabled}
                                onClick={onDownloadFileHandler}
                                use="primary"
                                size="medium">
                                Загрузить файлы
                            </Button>
                            <Button onClick={setPreviousStep} data-tid="PreviousStepButton" size="medium">
                                Назад
                            </Button>
                        </Gapped>
                    </StepPage>
                    <StepPage
                        visible={
                            step !== PatchFufSteps.SelectCombineFufMethod &&
                            step !== PatchFufSteps.SelectCombineSumMethod
                        }>
                        <Gapped gap={16}>
                            {modalSettings?.useTransfersAndCheckUnitExist &&
                            modalSettings?.rsvSection === 3 &&
                            !isDynamicAndSelectCombineSumMethod &&
                            modalSettings?.currentInstancesCount !== 0 ? (
                                <Button
                                    data-tid={"PickSumMethodButton"}
                                    disabled={isButtonLoadDisabled}
                                    onClick={onPickSumMethod}
                                    use="primary"
                                    size="medium">
                                    Выбрать суммы
                                </Button>
                            ) : (
                                <Button
                                    data-tid={"UploadButton"}
                                    disabled={isButtonLoadDisabled}
                                    onClick={onDownloadFileHandler}
                                    use="primary"
                                    size="medium">
                                    Загрузить файлы
                                    {isHaveValidAndInvalidFile && !isButtonLoadDisabled && " без ошибок"}
                                </Button>
                            )}

                            {!loading && (
                                <Button onClick={onClose} data-tid={"CloseModalButton"} size="medium">
                                    Закрыть
                                </Button>
                            )}
                        </Gapped>
                    </StepPage>
                </Modal.Footer>
            </StepPage>
        </Modal>
    );
};
